import Constants from 'api-constants'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import { serialize } from 'helpers'
import { of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'
import Fetch from '../fetch'

/**
 * A class for managing user's preferences:
 *
 *      Managing favourites lists (collections) and favourite items (collection items)
 *      Managing bookmarks
 *      Managing reminders
 *
 * {@link https://docs.nagra.com/doc/otv/otvdoc2051/apis/user-activity-vault-uav-api-documentation#/ Nagra docs}
 *
 * @class
 */
export default class UserPreferencesApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.config = config
    }

    static FAVOURITES_LIST_NAMES = {
        VOD: 'vod_favourites',
        CHANNEL: 'channel_favourites',
        DVR: 'dvr_favourites',
    }

    static WATCHLIST_AND_RENTALS = 'WATCHLIST_AND_RENTALS'
    static WATCHLIST_AND_RENTALS_PREFIX = 'WATCHLIST_AND_RENTALS_'

    static FAVOURITE_CHANNELS = 'FAVOURITE_CHANNELS'
    static FAVOURITE_CHANNELS_PREFIX = 'FAVOURITE_CHANNELS_'

    /**
     * Needed for filtering bookmarks.
     * That is because we use `BookmarksApi` for storing parental control level which uses `PARENTAL_CONTROL_SETTING` tag
     */
    static BOOKMARK_TAG = 'BOOKMARK'

    static LEGACY_BOOKMARK_TAG = 'editorial' // used for the migration from accedo
    static LEGACY_BOOKMARK_SERIES_TAG = 'series' // used for the migration from accedo

    /**
     * property for storing user's favourites lists IDs
     *
     * This object exists before refreshing the page but it's ok for stb
     * @type {Object}
     * @example { 'UserID0000': { 'vod_favourites': '123456', 'dvr_favourites': '54321' } }
     */
    _collectionsCache = {}

    _nagraApiUrl = ConfigHelper.getInstance().getConfig('urls').apiUrl

    // #region ---------- Favourites lists management ---------
    /**
     * Gets all favourites lists for a given account
     * @param {Number} offset - the number of records to skip. For pagination;
     * @param {Number} limit - the number of records to return
     * @param {Object} filter - a filter allowing filtering by the tag entity @example{tag: "some tag"}
     * @returns {Observable<Array>} returns an array of the user's favourites lists
     */
    getCollection(collectionName, offset = 0, limit = 20) {
        const filter = { tag: collectionName }
        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    url: `${
                        this._nagraApiUrl
                    }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/favouriteslists${serialize(
                        {
                            limit,
                            offset,
                            filter: JSON.stringify(filter),
                        }
                    )}`,
                    method: 'GET',
                    headers: { ...this._getRequestHeaders() },
                    log: 'GET USER COLLECTIONS',
                })
            },
            {
                limit,
                offset,
                responseResource: 'favouritesLists',
            }
        ).pipe(mergeMap((favouritesLists) => of(favouritesLists)))
    }

    /**
     * Creates a new favourites list for an account
     * @param {UserPreferencesApi.FAVOURITES_LIST_NAMES} collectionName - collection name for filtering
     * @returns {Observable<Object>} returns added list details
     */
    createCollection(collectionName) {
        return this.fetch({
            url: `${
                this._nagraApiUrl
            }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/favouriteslists`,
            method: 'POST',
            headers: {
                ...this._getRequestHeaders(),
                tenantId: ConfigHelper.getInstance().getConfig('nagra').tenantId,
            },
            body: {
                name: collectionName,
                account: DataHelper.getInstance().getAccountId(),
                deviceId: this.config.uniqueDeviceId,
                tags: [collectionName],
            },
            log: 'CREATE USER COLLECTION',
        }).pipe(mergeMap(({ response } = {}) => of(response)))
    }

    /**
     * Deletes an existing account's favourites list and all entries
     * @param {String} collectionId - the id of the collection
     * @returns {Observable<Boolean>} returns true in case of successful deleting
     */
    deleteCollection(collectionId) {
        return this.fetch({
            url: `${
                this._nagraApiUrl
            }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/favouriteslists/${collectionId}`,
            method: 'DELETE',
            headers: { ...this._getRequestHeaders() },
            log: 'DELETE USER COLLECTION',
        }).pipe(mergeMap(() => of(true)))
    }
    // #endregion

    // #region ---------- Favourites content management ---------
    /**
     * Gets all favourites from a list for a given account
     * @param {String} collectionId - the id of the collection
     * @returns {Observable<Array>} return an array of a user's favourites
     */
    getCollectionItems(collectionId, offset = 0, limit = 20) {
        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    url: `${
                        this._nagraApiUrl
                    }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/favouriteslists/${collectionId}${serialize(
                        {
                            limit,
                            offset,
                            sort: 'modifiedDateDesc',
                        }
                    )}`,
                    method: 'GET',
                    headers: { ...this._getRequestHeaders() },
                    log: 'GET USER COLLECTION ITEMS',
                })
            },
            {
                limit,
                offset,
                responseResource: 'favourites',
            }
        ).pipe(mergeMap((favourites) => of(favourites)))
    }

    /**
     * Adds a favourite to an account's favourites list
     * @param {String} collectionName - collection name for filtering
     * @returns {Observable<Object>} returns added content details
     */
    addCollectionItem(collectionName, itemId) {
        return this.getCollectionIdByName(collectionName).pipe(
            mergeMap((collectionId) => {
                return this.fetch({
                    url: `${
                        this._nagraApiUrl
                    }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/favouriteslists/${collectionId}/content/${itemId}`,
                    method: 'PUT',
                    headers: { ...this._getRequestHeaders() },
                    body: {
                        favouriteListId: collectionId,
                        contentId: itemId,
                    },
                    log: 'ADD USER COLLECTION ITEM',
                }).pipe(mergeMap(({ response } = {}) => of(response)))
            })
        )
    }

    /**
     * Removes a favourite from an account's favourites list
     * @param {String} collectionName - collection name for filtering
     * @returns {Observable<Boolean>} returns true in case of successful deleting
     */
    deleteCollectionItem(collectionName, itemId) {
        return this.getCollectionIdByName(collectionName).pipe(
            mergeMap((collectionId) => {
                return this.fetch({
                    url: `${
                        this._nagraApiUrl
                    }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/favouriteslists/${collectionId}/content/${itemId}`,
                    method: 'DELETE',
                    headers: { ...this._getRequestHeaders() },
                    log: 'DELETE USER COLLECTION ITEM',
                }).pipe(mergeMap(() => of(true)))
            })
        )
    }
    // #endregion

    // #region ---------- Bookmarks management ----------

    /**
     * Gets all bookmarks for a given account
     * @param {String} tag - tag phrase for filtering (especially for Parental Level)
     * @returns {Observable<Array>} returns an array of a given account bookmarks
     */
    getBookmarks(tag = this.BOOKMARK_TAG) {
        const filter = { tag }
        return this.fetch({
            url: `${
                this._nagraApiUrl
            }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/bookmarks${serialize(
                {
                    filter: JSON.stringify(filter),
                    sort: 'modifiedDateDesc',
                }
            )}`,
            method: 'GET',
            headers: { ...this._getRequestHeaders() },
            log: `GET USER BOOKMARKS ${tag}`,
        }).pipe(mergeMap(({ response } = {}) => of(response.bookmarks)))
    }

    /**
     * Creates or updates an existing bookmark for account
     * @param {Number} position - the position of the bookmark, in seconds from the beginning of the content
     * @param {String} contentId - the contentId the bookmark reference
     * @param {String} programType - program type
     * @param {Object<string, any>} metadata - additional data for storing at bookmarks (especially for Parental Level)
     * @param {Array<string>} tags - optional tags for filtering in get request (especially for Parental Level)
     * @returns {Observable<Boolean>} returns true in case of successful adding
     */
    addBookmark(
        position,
        contentId,
        programType,
        metadata = {},
        tags = [this.BOOKMARK_TAG || 'BOOKMARK']
    ) {
        const accountId = DataHelper.getInstance().getAccountId()
        return this.fetch({
            url: `${this._nagraApiUrl}/useractivityvault/v1/clientdata/account/${accountId}/bookmarks/${contentId}`,
            method: 'PUT',
            headers: { ...this._getRequestHeaders() },
            body: {
                contentId,
                account: accountId,
                position,
                contentType: programType,
                metadata,
                tags,
            },
            log: 'ADD USER BOOKMARK',
        }).pipe(mergeMap(() => of(true)))
    }

    /**
     * Deletes an existing bookmark for a given account
     * @param {String} contentId - the contentId the bookmark reference
     * @returns {Observable<Boolean>} returns true in case of successful deleting
     */
    deleteBookmark(contentId) {
        return this.fetch({
            url: `${
                this._nagraApiUrl
            }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/bookmarks/${contentId}`,
            method: 'DELETE',
            headers: { ...this._getRequestHeaders() },
            log: 'DELETE USER BOOKMARKS',
        }).pipe(mergeMap(() => of(true)))
    }
    // #endregion

    // #region ---------- Reminders management ----------

    /**
     * Gets reminders for a given user
     * @returns {Observable<Array>} returns an array of a given account reminders
     */
    getReminders() {
        return this.fetch({
            url: `${
                this._nagraApiUrl
            }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/reminders`,
            method: 'GET',
            headers: { ...this._getRequestHeaders() },
            log: 'GET USER REMINDERS',
        }).pipe(mergeMap(({ response } = {}) => of(response.reminders)))
    }

    /**
     * Creates a new reminder for an account
     * @param {String} eventId - id of the reminder reference
     * @param {String} channelId - channel id
     * @param {Number} startTime - start time for reminding
     * @param {Number} endTime - end time for reminding
     * @returns {Observable<Boolean>} returns true in case of successful adding
     */
    addReminder(eventId, channelId, startTime, endTime) {
        const accountId = DataHelper.getInstance().getAccountId()
        return this.fetch({
            url: `${this._nagraApiUrl}/useractivityvault/v1/clientdata/account/${accountId}/reminders`,
            method: 'POST',
            headers: { ...this._getRequestHeaders() },
            body: {
                account: accountId,
                contentId: eventId,
                contentType: 'reminder',
                metadata: {
                    channelId,
                    startTime,
                    endTime,
                },
            },
            log: 'ADD USER REMINDER',
        }).pipe(mergeMap(({ response } = {}) => of(response)))
    }

    /**
     * Deletes an existing reminder for a given user
     * @param {String} reminderId - the id of the reminder
     * @returns {Observable<Boolean>} returns true in case of successful deleting
     */
    deleteReminder(reminderId) {
        return this.fetch({
            url: `${
                this._nagraApiUrl
            }/useractivityvault/v1/clientdata/account/${DataHelper.getInstance().getAccountId()}/reminders/${reminderId}`,
            method: 'DELETE',
            log: 'DELETE USER REMINDER',
            headers: { ...this._getRequestHeaders() },
        }).pipe(mergeMap(() => of(true)))
    }
    // #endregion

    // #region ---------- toolbox ----------

    /**
     * Returns an array of user's favourites by type {@link UserPreferencesApi.FAVOURITES_LIST_NAMES}
     * @param {UserPreferencesApi.FAVOURITES_LIST_NAMES} collectionName - collection name for filtering
     * @returns {Array<String>} an array of user's favourites
     */
    getItemsByCollectionName(collectionName) {
        return this.getCollectionIdByName(collectionName).pipe(
            mergeMap((collectionId = '') => {
                if (!collectionId) return of([])
                return this.getCollectionItems(collectionId).pipe(
                    mergeMap((favourites = []) => {
                        if (!favourites.length) return of([])
                        return of(favourites.map((favourite) => favourite.contentId))
                    })
                )
            })
        )
    }

    // TODO: ask Nagra if it is possible to return the ID of a new list to reduce amount of http calls
    /**
     * Returns an Id of a user's favourites list.
     *
     * If a list of the given type does not exist function creates a new list and returns its id
     *
     * Also supports caching of users's collections
     * @param {UserPreferencesApi.FAVOURITES_LIST_NAMES} collectionName - collection name for filtering
     * @returns {Observable<String>} an id of the user's favourites list
     */
    getCollectionIdByName(collectionName) {
        const collectionIdFromCache = this._getCollectionFromCache(collectionName)

        return collectionIdFromCache
            ? of(collectionIdFromCache)
            : this.getCollection(collectionName).pipe(
                  mergeMap((collection = []) => {
                      // if collection is exist returns its id
                      if (collection.length) {
                          this._addCollectionToCache(collectionName, collection[0].id)
                          return of(collection[0].id)
                      }
                      // else creates a new one
                      return this.createCollection(collectionName).pipe(
                          mergeMap((collectionDetails) =>
                              // returns list id by filtering with a tag which is consist of list name
                              this.getCollection(collectionDetails.name).pipe(
                                  mergeMap((collection) => {
                                      this._addCollectionToCache(collectionName, collection[0].id)
                                      return of(collection[0].id)
                                  })
                              )
                          )
                      )
                  })
              )
    }

    _addCollectionToCache(collectionName, collectionId) {
        const accountId = DataHelper.getInstance().getAccountId()
        this._collectionsCache[accountId] = {
            ...this._collectionsCache[accountId],
            [collectionName]: collectionId,
        }
    }

    _getCollectionFromCache(collectionName) {
        const accountId = DataHelper.getInstance().getAccountId()
        if (
            this._collectionsCache[accountId] &&
            this._collectionsCache[accountId][collectionName]
        ) {
            return this._collectionsCache[accountId][collectionName]
        }
    }

    _getRequestHeaders() {
        return {
            'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
            'Client-token': DataHelper.getInstance().getSecondaryAccessToken(),
        }
    }
    // #endregion
}
